import {
	Autocomplete,
	Box,
	Button,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	TextField,
	Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import type { IB2B_list } from "src/content/modules/B2B/type/b2bType";
import type { IOfflineExhibitorListItem } from "src/content/modules/exhibitor/type/offlineExhibitorType";
import type { IVisitorList } from "src/content/modules/visitor/type/visitorType";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import type { ICreateParticipantPayload } from "src/Types/module/raffleDraw/raffleDraw";

interface Props {
	handleClose: () => void;
}

const CreateParticipantModal: React.FC<Props> = ({ handleClose }) => {
	const [userType, setUserType] = useState<"visitor" | "b2b" | "exhibitor">(
		"visitor"
	);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedVisitor, setSelectedVisitor] = useState<IVisitorList | null>(
		null
	);
	const [selectedB2B, setSelectedB2B] = useState<IB2B_list | null>(null);
	const [selectedExhibitor, setSelectedExhibitor] =
		useState<IOfflineExhibitorListItem | null>(null);

	// Fetch visitors when userType === 'visitor'
	const { data: visitorData, isLoading: isVisitorLoading } =
		useGetQueryWithTotal<IVisitorList[]>({
			endPoint: "/api/admin/visitor",
			queryKeys: ["visitor-raffle-search", searchQuery],
			params: { name: searchQuery },
			enabled: userType === "visitor",
		});

	// Fetch B2B members when userType === 'b2b'
	const { data: b2bData, isLoading: isB2BLoading } = useGetQueryWithTotal<
		IB2B_list[]
	>({
		endPoint: "/api/fair/admin/b2b",
		queryKeys: ["b2b-raffle-search", searchQuery],
		params: { name: searchQuery },
		enabled: userType === "b2b",
	});

	// Fetch exhibitors when userType === 'exhibitor'
	const { data: exhibitorData, isLoading: isExhibitorLoading } =
		useGetQueryWithTotal<IOfflineExhibitorListItem[]>({
			endPoint: "/api/v1/admin/offline-exhibitor",
			queryKeys: ["exhibitor-raffle-search", searchQuery],
			params: {
				limit: 50,
				...(searchQuery.trim()
					? { searchQuery: searchQuery.trim(), name: searchQuery.trim() }
					: {}),
			},
			enabled: userType === "exhibitor",
		});

	const { mutate: createParticipant, isPending, isSuccess } = useApiMutation<
		ICreateParticipantPayload,
		void
	>({
		method: "POST",
		endPoint: "/api/raffle-draw/admin/participant",
		invalidateQueries: ["raffle-participant"],
	});

	useEffect(() => {
		if (isSuccess) {
			handleClose();
		}
	}, [isSuccess]);

	const handleUserTypeChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const newType = e.target.value as "visitor" | "b2b" | "exhibitor";
		setUserType(newType);
		setSelectedVisitor(null);
		setSelectedB2B(null);
		setSelectedExhibitor(null);
		setSearchQuery("");
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		let userId: number | null = null;
		if (userType === "visitor" && selectedVisitor?.visitor_id) {
			userId = Number(selectedVisitor.visitor_id);
		} else if (userType === "b2b" && selectedB2B?.b2b_id) {
			userId = Number(selectedB2B.b2b_id);
		} else if (
			userType === "exhibitor" &&
			selectedExhibitor?.id
		) {
			userId = Number(selectedExhibitor.id);
		}

		if (!userId) {
			const label =
				userType === "visitor"
					? "visitor"
					: userType === "b2b"
						? "B2B member"
						: "exhibitor";
			alert(`Please select a ${label}.`);
			return;
		}

		createParticipant({
			user_id: userId,
			user_type: userType,
		});
	};

	return (
		<Box
			component="form"
			onSubmit={handleSubmit}
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				pt: 1,
				pb: 0.5,
			}}
		>
			<FormControl component="fieldset">
				<FormLabel component="legend" sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
					Participant User Type
				</FormLabel>
				<RadioGroup
					row
					value={userType}
					onChange={handleUserTypeChange}
					sx={{ mt: 0.5 }}
				>
					<FormControlLabel
						value="visitor"
						control={<Radio size="small" />}
						label={<Typography variant="body2">Visitor</Typography>}
					/>
					<FormControlLabel
						value="b2b"
						control={<Radio size="small" />}
						label={<Typography variant="body2">B2B Member</Typography>}
					/>
					<FormControlLabel
						value="exhibitor"
						control={<Radio size="small" />}
						label={<Typography variant="body2">Exhibitor</Typography>}
					/>
				</RadioGroup>
			</FormControl>

			{userType === "visitor" && (
				<Autocomplete<IVisitorList, false, false, false>
					options={visitorData?.data || []}
					value={selectedVisitor}
					loading={isVisitorLoading}
					getOptionLabel={(option) => {
						if (!option) return "";
						const name =
							`${option.visitor_first_name || ""} ${option.visitor_last_name || ""}`.trim() ||
							"Visitor";
						const phone = option.visitor_phone ? ` (${option.visitor_phone})` : "";
						const org = option.visitor_organization
							? ` - ${option.visitor_organization}`
							: "";
						return `${name}${phone}${org}`;
					}}
					isOptionEqualToValue={(opt, val) => opt.visitor_id === val.visitor_id}
					onInputChange={(_, val, reason) => {
						if (reason === "input") setSearchQuery(val);
						if (reason === "clear") setSearchQuery("");
					}}
					onChange={(_, val) => setSelectedVisitor(val)}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Select Visitor *"
							placeholder="Search by visitor name or phone"
							size="small"
							required
							InputLabelProps={{ shrink: true }}
						/>
					)}
				/>
			)}

			{userType === "b2b" && (
				<Autocomplete<IB2B_list, false, false, false>
					options={b2bData?.data || []}
					value={selectedB2B}
					loading={isB2BLoading}
					getOptionLabel={(option) => {
						if (!option) return "";
						const org = option.b2b_organization ? `${option.b2b_organization} - ` : "";
						const name = `${option.b2b_first_name || ""} ${option.b2b_last_name || ""}`.trim();
						return `${org}${name || "B2B Member"} (#${option.b2b_id})`;
					}}
					isOptionEqualToValue={(opt, val) => opt.b2b_id === val.b2b_id}
					onInputChange={(_, val, reason) => {
						if (reason === "input") setSearchQuery(val);
						if (reason === "clear") setSearchQuery("");
					}}
					onChange={(_, val) => setSelectedB2B(val)}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Select B2B Member *"
							placeholder="Search by company or member name"
							size="small"
							required
							InputLabelProps={{ shrink: true }}
						/>
					)}
				/>
			)}

			{userType === "exhibitor" && (
				<Autocomplete<IOfflineExhibitorListItem, false, false, false>
					options={exhibitorData?.data || []}
					value={selectedExhibitor}
					loading={isExhibitorLoading}
					getOptionLabel={(option) => {
						if (!option) return "";
						const name = option.name || "Exhibitor";
						const no = option.exhibitor_no ? ` (${option.exhibitor_no})` : "";
						const email =
							option.company_email || option.email
								? ` - ${option.company_email || option.email}`
								: "";
						return `${name}${no}${email}`;
					}}
					isOptionEqualToValue={(opt, val) => opt.id === val.id}
					onInputChange={(_, val, reason) => {
						if (reason === "input") setSearchQuery(val);
						if (reason === "clear") setSearchQuery("");
					}}
					onChange={(_, val) => setSelectedExhibitor(val)}
					renderOption={(props, option) => (
						<Box
							component="li"
							{...props}
							key={option.id}
							sx={{
								display: "flex !important",
								flexDirection: "column",
								alignItems: "flex-start !important",
								py: 1,
								px: 1.5,
								borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
							}}
						>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									width: "100%",
									alignItems: "center",
								}}
							>
								<Typography variant="body2" fontWeight={600} color="text.primary">
									{option.name || "Exhibitor"}
								</Typography>
								{option.exhibitor_no && (
									<Typography
										variant="caption"
										sx={{
											fontWeight: 700,
											color: "primary.main",
											bgcolor: "primary.lighter",
											px: 0.8,
											py: 0.2,
											borderRadius: 0.5,
										}}
									>
										{option.exhibitor_no}
									</Typography>
								)}
							</Box>
							{(option.company_email || option.email) && (
								<Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
									{option.company_email || option.email}
								</Typography>
							)}
						</Box>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Select Exhibitor *"
							placeholder="Search by company name or exhibitor no"
							size="small"
							required
							InputLabelProps={{ shrink: true }}
						/>
					)}
				/>
			)}

			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					gap: 1.5,
					mt: 1,
				}}
			>
				<Button
					variant="outlined"
					color="inherit"
					onClick={handleClose}
					disabled={isPending}
				>
					<span>Cancel</span>
				</Button>
				<Button
					type="submit"
					variant="contained"
					color="primary"
					disabled={isPending}
					sx={{ minWidth: 140, fontWeight: 600, textTransform: "none" }}
				>
					<span>{isPending ? "Adding..." : "Add Participant"}</span>
				</Button>
			</Box>
		</Box>
	);
};

export default CreateParticipantModal;
